import { Injectable } from '@angular/core';

const DEFAULTS = {
  primary: '#2563eb',
  primaryBtn: '#2563eb',
  primaryBtnHover: '#1d4ed8',
  defaultBtn: '#2563eb',
  defaultBtnHover: '#1d4ed8',
  warningBtn: '#d97706',
  warningBtnHover: '#b45309',
  linkColor: '#2563eb'
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  applyFromStorage(): void {
    const raw = localStorage.getItem('tenant_config');
    if (raw) {
      try {
        this.apply(JSON.parse(raw));
      } catch {
        this.reset();
      }
    } else {
      this.reset();
    }
    // Restore dark mode only if user explicitly chose dark — default is light
    const savedMode = localStorage.getItem('theme_mode');
    if (savedMode === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      // Always start in light mode unless user explicitly chose dark
      document.documentElement.removeAttribute('data-theme');
    }
  }

  get isDark(): boolean {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  }

  toggleDark(): void {
    if (this.isDark) {
      document.documentElement.removeAttribute('data-theme');
      localStorage.removeItem('theme_mode');   // no key = light (default)
    } else {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme_mode', 'dark');
    }
  }

  apply(config: any): void {
    let portalConfig: any = {};
    try {
      if (config?.portal_config) portalConfig = JSON.parse(config.portal_config);
    } catch {}
    const theme = portalConfig?.theme || {};

    const primary = theme?.primary_color || DEFAULTS.primary;
    const primaryBtn = theme?.accent_color || DEFAULTS.primaryBtn;
    const defaultBtn = theme?.button_color || DEFAULTS.defaultBtn;
    const warningBtn = theme?.warning_color || DEFAULTS.warningBtn;
    const linkColor = theme?.link_color || defaultBtn;

    this.setVars({
      '--theme-primary': primary,
      '--theme-primary-focus': this.hexToRgba(primary, 0.2),
      '--theme-primary-btn': primaryBtn,
      '--theme-primary-btn-hover': theme?.accent_hover || this.darken(primaryBtn, 0.15),
      '--theme-default-btn': defaultBtn,
      '--theme-default-btn-hover': theme?.button_hover || this.darken(defaultBtn, 0.15),
      '--theme-warning-btn': warningBtn,
      '--theme-warning-btn-hover': theme?.warning_hover || this.darken(warningBtn, 0.15),
      '--theme-link': linkColor
    });
  }

  reset(): void {
    this.setVars({
      '--theme-primary': DEFAULTS.primary,
      '--theme-primary-focus': this.hexToRgba(DEFAULTS.primary, 0.2),
      '--theme-primary-btn': DEFAULTS.primaryBtn,
      '--theme-primary-btn-hover': DEFAULTS.primaryBtnHover,
      '--theme-default-btn': DEFAULTS.defaultBtn,
      '--theme-default-btn-hover': DEFAULTS.defaultBtnHover,
      '--theme-warning-btn': DEFAULTS.warningBtn,
      '--theme-warning-btn-hover': DEFAULTS.warningBtnHover,
      '--theme-link': DEFAULTS.linkColor
    });
  }

  private setVars(vars: Record<string, string>): void {
    const root = document.documentElement;
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  }

  private hexToRgba(hex: string, alpha: number): string {
    const c = hex.replace('#', '');
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private darken(hex: string, amount: number): string {
    const c = hex.replace('#', '');
    const r = Math.max(0, Math.round(parseInt(c.slice(0, 2), 16) * (1 - amount)));
    const g = Math.max(0, Math.round(parseInt(c.slice(2, 4), 16) * (1 - amount)));
    const b = Math.max(0, Math.round(parseInt(c.slice(4, 6), 16) * (1 - amount)));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
}
